export function useMeta() {
	const {onGdprUpdate, gdprApproved} = useGdpr();
	const gdprScriptRegistry = [];
	let gdprWatcher = false;
	const delayedScripts = [];

	// Handle delayed script execution on user interaction
	function handleDelayedScripts() {
		if (process.server) return;

		// Execute all delayed scripts
		delayedScripts.forEach(script => {
			// Remove delay flag to prevent infinite loop
			const scriptCopy = {...script};
			delete scriptCopy.delay;
			addScriptImmediate(scriptCopy);
		});

		// Clear the delayed scripts array
		delayedScripts.length = 0;
	}

	function installScript(script) {
		return new Promise((resolve, reject) => {
			if (process.server) return resolve();
			if (!script?.key) {
				useLog('Script key is not defined', 'error');
				return reject(new Error('Script key is not defined'));
			}

			// Check if the script already exists
			if (document.querySelector(`script[data-key="${script.key}"]`)) {
				if (script.onInstall) script.onInstall();
				resolve();
				return;
			}

			// Create a new script element
			const scriptElement = document.createElement(script.noscript ? 'noscript' : 'script');
			scriptElement.setAttribute('data-key', script.key);

			if (script.src) {
				scriptElement.src = script.src;
			} else if (script.innerHTML) {
				scriptElement.innerHTML = script.innerHTML;
			}

			if (script.async) scriptElement.async = script.async;
			if (script.defer) scriptElement.defer = script.defer;
			if (script.id) scriptElement.id = script.id;
			if (script.type) scriptElement.type = script.type;
			if (script.integrity) scriptElement.integrity = script.integrity;
			if (script.crossorigin) scriptElement.crossorigin = script.crossorigin;
			if (script.dataAttributes && typeof script.dataAttributes === 'object') {
				Object.entries(script.dataAttributes).forEach(([key, value]) => {
					scriptElement.setAttribute(`data-${key}`, value);
				});
			}

			scriptElement.onload = () => {
				if (script.onInstall) script.onInstall();
				resolve();
			};

			scriptElement.onerror = error => {
				useLog(`Failed to load script: ${script.key}`, 'error');
				reject(error);
			};

			if (script.noscript) {
				document.body.insertBefore(scriptElement, document.body.firstChild);
			} else {
				let targetElement = document.body;
				if (script.appendTo) {
					const customTarget = document.querySelector(script.appendTo);
					if (customTarget) {
						targetElement = customTarget;
					} else {
						useLog(`Invalid appendTo selector: ${script.appendTo}`, 'warn');
					}
				}
				targetElement.appendChild(scriptElement);
			}
		});
	}

	function uninstallScript(script) {
		if (process.server) return;
		if (!script?.key) return useLog('Script key is not defined', 'error');

		// Remove script from DOM if it exists
		const scriptElement = document.querySelector((script.noscript ? 'noscript' : 'script') + `[data-key="${script.key}"]`);
		if (scriptElement) {
			scriptElement.remove();
			if (script.onUninstall) script.onUninstall();
		}

		// Remove script from delayed scripts array if it exists there
		const delayedIndex = delayedScripts.findIndex(delayedScript => delayedScript.key === script.key);
		if (delayedIndex !== -1) {
			delayedScripts.splice(delayedIndex, 1);
		}
	}

	// Add script immediately without delay
	async function addScriptImmediate(script) {
		if (process.server) return Promise.resolve();
		if (!script) return useLog('Script is not defined', 'error');

		// If GDPR is not required, just add the script
		if (!script.gdpr || gdprApproved(script.gdpr)) {
			await installScript(script);
			return;
		}

		gdprScriptRegistry.push(script);
		if (!gdprWatcher) {
			setGdprWatcher();
			gdprWatcher = true;
		}

		return;
	}

	async function addScript(script) {
		if (process.server) return Promise.resolve();
		if (!script) return useLog('Script is not defined', 'error');

		// Handle array of scripts
		if (Array.isArray(script)) {
			const promises = script.map(s => addScript(s));
			return Promise.all(promises);
		}

		// Check if script should be delayed until user interaction
		if (script.delay) {
			delayedScripts.push(script);
			// Use global user interaction state
			const {userInitialized} = useUtils();
			if (userInitialized.value) {
				handleDelayedScripts();
			} else {
				const unwatch = watch(userInitialized, newValue => {
					if (newValue) {
						handleDelayedScripts();
						unwatch();
					}
				});
			}
			return Promise.resolve();
		}

		// Execute script immediately
		return addScriptImmediate(script);
	}

	function setGdprWatcher() {
		onGdprUpdate(
			'all',
			data => {
				for (const script of gdprScriptRegistry) {
					if (script.gdprReload) {
						return reloadNuxtApp({ttl: 2000});
					}
					if (data?.includes(script.gdpr)) {
						installScript(script);
						if (script?.onGrantConsent) script.onGrantConsent();
					} else {
						uninstallScript(script);
						if (script?.onRevokeConsent) script.onRevokeConsent();
					}
				}
			},
			{immediate: false}
		);
	}

	function waitForWindowProperty(property, callback, options = {}) {
		if (process.server) return;
		if (typeof property !== 'string') throw new Error('Property must be a string representing the key on window');

		const {
			intervalTime = 1000, // Time between checks (in milliseconds)
			maxAttempts = 5, // Maximum number of checks before stopping
			onFailure = () => {}, // Optional callback for when the limit is reached
			delay = false, // Wait for user interaction before starting checks
		} = options;

		// Function to start the actual property checking
		function startChecking() {
			// If no callback is provided, return a Promise for await usage
			if (!callback) {
				return new Promise((resolve, reject) => {
					let attempts = 0;
					const interval = setInterval(() => {
						attempts++;
						if (typeof window !== 'undefined' && window[property] !== undefined) {
							clearInterval(interval);
							resolve(window[property]);
						} else if (attempts >= maxAttempts) {
							clearInterval(interval);
							onFailure();
							reject(new Error(`Window property '${property}' not found after ${maxAttempts} attempts`));
						}
					}, intervalTime);
				});
			}

			// Original callback-based implementation
			if (typeof callback !== 'function') throw new Error('Callback must be a function');

			let attempts = 0;
			const interval = setInterval(() => {
				attempts++;
				if (typeof window !== 'undefined' && window[property] !== undefined) {
					clearInterval(interval);
					callback(window[property]);
				} else if (attempts >= maxAttempts) {
					clearInterval(interval);
					onFailure();
				}
			}, intervalTime);
		}

		// If delay is enabled, wait for user interaction first
		if (delay) {
			const {userInitialized} = useUtils();

			if (userInitialized.value) {
				return startChecking();
			} else {
				// If using Promise mode
				if (!callback) {
					return new Promise((resolve, reject) => {
						const unwatch = watch(userInitialized, newValue => {
							if (newValue) {
								startChecking().then(resolve).catch(reject);
								unwatch();
							}
						});
					});
				} else {
					// For callback mode
					const unwatch = watch(userInitialized, newValue => {
						if (newValue) {
							startChecking();
							unwatch();
						}
					});
				}
			}
		} else {
			// Start checking immediately if not waiting for user activity
			return startChecking();
		}
	}

	return {installScript, uninstallScript, addScript, waitForWindowProperty};
}
